// src/pages/trading-portal/MarketDataIngestion/MacroEconomicPage.js
// Macro-economic indicators dashboard and monitor config page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Tabs,
    Tab,
    Switch,
    FormControlLabel,
    Tooltip,
} from '@mui/material';
import {
    AccountBalance,
    TrendingUp,
    TrendingDown,
    ArrowUpward,
    ArrowDownward,
    Refresh,
    Edit,
    Assessment,
    Event,
    Speed,
    CloudDownload,
    Storage,
    Delete,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    MacroIndicatorType,
    MacroIndicatorTypeLabels,
    MacroIndicatorTypeColors,
    MacroDataSource,
    MacroDataSourceLabels,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

// Indicator groupings for dashboard cards
const INDICATOR_GROUPS = [
    {
        label: 'Fed Liquidity',
        icon: AccountBalance,
        color: '#3b82f6',
        indicators: [
            MacroIndicatorType.FED_BALANCE_SHEET,
            MacroIndicatorType.TGA,
            MacroIndicatorType.REVERSE_REPO,
            MacroIndicatorType.FED_NET_LIQUIDITY,
        ],
    },
    {
        label: 'VIX',
        icon: Speed,
        color: '#ef4444',
        indicators: [
            MacroIndicatorType.VIX,
            MacroIndicatorType.VIX9D,
            MacroIndicatorType.VIX3M,
            MacroIndicatorType.VIX6M,
        ],
    },
    {
        label: 'Treasury Rates',
        icon: Assessment,
        color: '#6366f1',
        indicators: [
            MacroIndicatorType.TREASURY_2Y,
            MacroIndicatorType.TREASURY_5Y,
            MacroIndicatorType.TREASURY_10Y,
            MacroIndicatorType.TREASURY_30Y,
        ],
    },
    {
        label: 'Other',
        icon: Event,
        color: '#10b981',
        indicators: [
            MacroIndicatorType.FED_FUNDS_RATE,
            MacroIndicatorType.YIELD_CURVE_2Y10Y,
            MacroIndicatorType.M2_MONEY_SUPPLY,
        ],
    },
];

const MacroEconomicPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Dashboard state
    const [dashboardData, setDashboardData] = useState({
        latest_values: {},
        latest_net_liquidity: null,
        monitor_configs: [],
    });
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [error, setError] = useState(null);

    // Monitor configs state
    const [monitorConfigs, setMonitorConfigs] = useState([]);
    const [configsLoading, setConfigsLoading] = useState(true);
    const [configPage, setConfigPage] = useState(0);
    const [configRowsPerPage, setConfigRowsPerPage] = useState(10);

    // Macro data records state
    const [macroDataRecords, setMacroDataRecords] = useState([]);
    const [macroDataLoading, setMacroDataLoading] = useState(false);
    const [macroDataPage, setMacroDataPage] = useState(0);
    const [macroDataRowsPerPage, setMacroDataRowsPerPage] = useState(10);
    const [macroDataTotal, setMacroDataTotal] = useState(0);
    const [macroDataFilter, setMacroDataFilter] = useState('');
    const [macroDataSourceFilter, setMacroDataSourceFilter] = useState('');

    // Dialog states
    const [manualEntryOpen, setManualEntryOpen] = useState(false);
    const [editConfigOpen, setEditConfigOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [fetchingIndicator, setFetchingIndicator] = useState(null);

    // Manual entry form
    const [manualForm, setManualForm] = useState({
        indicator_type: '',
        value: '',
        previous_value: '',
        change_from_previous: '',
        change_percent: '',
        release_date: '',
        period_date: '',
        data_source: 'MANUAL',
    });
    const [manualFormErrors, setManualFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Edit config form
    const [editConfigForm, setEditConfigForm] = useState({
        refresh_interval_minutes: '',
        is_active: true,
        alert_on_release: false,
        alert_threshold_percent: '',
        data_source: '',
    });
    const [editConfigErrors, setEditConfigErrors] = useState({});

    // Permission check
    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-mdi-macro', 'view');
            const add = await hasPermission('tr-mdi-macro', 'add');
            const edit = await hasPermission('tr-mdi-macro', 'edit');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
        };
        checkPermissions();
    }, []);

    // Fetch dashboard data
    const fetchDashboard = useCallback(async () => {
        if (!canView) return;
        setDashboardLoading(true);
        setError(null);

        try {
            const response = await axiosInstance.get('/api/market-data-ingestion/macro/dashboard');
            if (response.data.success) {
                setDashboardData({
                    latest_values: response.data.data?.latest_values || response.data.latest_values || {},
                    latest_net_liquidity: response.data.data?.latest_net_liquidity || response.data.latest_net_liquidity || null,
                    monitor_configs: response.data.data?.monitor_configs || response.data.monitor_configs || [],
                });
            } else {
                setError(response.data.message || 'Failed to fetch dashboard data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch dashboard data');
        } finally {
            setDashboardLoading(false);
        }
    }, [canView]);

    // Fetch monitor configs
    const fetchMonitorConfigs = useCallback(async () => {
        if (!canView) return;
        setConfigsLoading(true);

        try {
            const response = await axiosInstance.get('/api/market-data-ingestion/macro/monitor-configs');
            if (response.data.success) {
                setMonitorConfigs(response.data.data || []);
            } else {
                setError(response.data.message || 'Failed to fetch monitor configs');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch monitor configs');
        } finally {
            setConfigsLoading(false);
        }
    }, [canView]);

    // Fetch macro data records
    const fetchMacroData = useCallback(async (page = 0, perPage = 10, indicatorFilter = '', sourceFilter = '') => {
        if (!canView) return;
        setMacroDataLoading(true);

        try {
            const params = new URLSearchParams({
                page: page + 1,
                per_page: perPage,
            });
            if (indicatorFilter) params.append('indicator_type', indicatorFilter);
            if (sourceFilter) params.append('data_source', sourceFilter);

            const response = await axiosInstance.get(`/api/market-data-ingestion/macro/data?${params}`);
            if (response.data.success) {
                setMacroDataRecords(response.data.data || []);
                setMacroDataTotal(response.data.pagination?.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch macro data');
        } finally {
            setMacroDataLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        if (canView) {
            fetchDashboard();
            fetchMonitorConfigs();
        }
    }, [fetchDashboard, fetchMonitorConfigs, canView]);

    // Tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        if (newValue === 1) {
            fetchMacroData(macroDataPage, macroDataRowsPerPage, macroDataFilter, macroDataSourceFilter);
        }
    };

    // Manual entry handlers
    const handleManualFormChange = (field) => (event) => {
        setManualForm(prev => ({ ...prev, [field]: event.target.value }));
        if (manualFormErrors[field]) setManualFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const resetManualForm = () => {
        setManualForm({
            indicator_type: '', value: '', previous_value: '', change_from_previous: '',
            change_percent: '', release_date: '', period_date: '', data_source: 'MANUAL',
        });
        setManualFormErrors({});
    };

    const handleManualEntryOpen = () => { resetManualForm(); setManualEntryOpen(true); };
    const handleManualEntryClose = () => { setManualEntryOpen(false); resetManualForm(); };

    const validateManualForm = () => {
        const errors = {};
        if (!manualForm.indicator_type) errors.indicator_type = 'Indicator type is required';
        if (!manualForm.value || isNaN(manualForm.value)) errors.value = 'Valid value is required';
        setManualFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleManualEntrySubmit = async () => {
        if (!validateManualForm()) return;
        setSubmitting(true);

        try {
            const payload = {
                indicator_type: manualForm.indicator_type,
                value: parseFloat(manualForm.value),
                previous_value: manualForm.previous_value ? parseFloat(manualForm.previous_value) : undefined,
                change_from_previous: manualForm.change_from_previous ? parseFloat(manualForm.change_from_previous) : undefined,
                change_percent: manualForm.change_percent ? parseFloat(manualForm.change_percent) : undefined,
                release_date: manualForm.release_date || undefined,
                period_date: manualForm.period_date || undefined,
                data_source: manualForm.data_source,
            };
            const response = await axiosInstance.post('/api/market-data-ingestion/macro/data', payload);

            if (response.data.success) {
                handleManualEntryClose();
                fetchDashboard();
                if (activeTab === 1) fetchMacroData(macroDataPage, macroDataRowsPerPage, macroDataFilter, macroDataSourceFilter);
            } else {
                setManualFormErrors({ submit: response.data.message || 'Failed to add data' });
            }
        } catch (err) {
            setManualFormErrors({ submit: err.response?.data?.message || 'Failed to add data' });
        } finally {
            setSubmitting(false);
        }
    };

    // Edit config handlers
    const handleEditConfigOpen = (config) => {
        setSelectedConfig(config);
        setEditConfigForm({
            refresh_interval_minutes: config.refresh_interval_minutes || '',
            is_active: config.is_active ?? true,
            alert_on_release: config.alert_on_release ?? false,
            alert_threshold_percent: config.alert_threshold_percent || '',
            data_source: config.data_source || '',
        });
        setEditConfigErrors({});
        setEditConfigOpen(true);
    };

    const handleEditConfigClose = () => {
        setEditConfigOpen(false);
        setSelectedConfig(null);
        setEditConfigErrors({});
    };

    const handleEditConfigFormChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setEditConfigForm(prev => ({ ...prev, [field]: value }));
        if (editConfigErrors[field]) setEditConfigErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleEditConfigSubmit = async () => {
        if (!selectedConfig) return;
        setSubmitting(true);

        try {
            const payload = {
                refresh_interval_minutes: editConfigForm.refresh_interval_minutes ? parseInt(editConfigForm.refresh_interval_minutes, 10) : undefined,
                is_active: editConfigForm.is_active,
                alert_on_release: editConfigForm.alert_on_release,
                alert_threshold_percent: editConfigForm.alert_threshold_percent ? parseFloat(editConfigForm.alert_threshold_percent) : undefined,
                data_source: editConfigForm.data_source || undefined,
            };
            const response = await axiosInstance.put(`/api/market-data-ingestion/macro/monitor-configs/${selectedConfig.id}`, payload);

            if (response.data.success) {
                handleEditConfigClose();
                fetchMonitorConfigs();
                fetchDashboard();
            } else {
                setEditConfigErrors({ submit: response.data.message || 'Failed to update config' });
            }
        } catch (err) {
            setEditConfigErrors({ submit: err.response?.data?.message || 'Failed to update config' });
        } finally {
            setSubmitting(false);
        }
    };

    // Fetch indicator data
    const handleFetchIndicator = async (indicatorType) => {
        setFetchingIndicator(indicatorType);
        try {
            const response = await axiosInstance.post(`/api/market-data-ingestion/macro/fetch/${indicatorType}`);
            if (response.data.success) {
                setError(null);
                setFetchAllResult({ type: 'success', message: response.data.message });
            } else {
                setError(response.data.message || 'Fetch failed');
            }
            fetchMonitorConfigs();
            fetchDashboard();
        } catch (err) {
            setError(err.response?.data?.message || 'Fetch failed');
        } finally {
            setFetchingIndicator(null);
        }
    };

    // Fetch all macro indicators
    const [fetchingAll, setFetchingAll] = useState(false);
    const [fetchAllResult, setFetchAllResult] = useState(null);

    const handleFetchAll = async () => {
        setFetchingAll(true);
        setFetchAllResult(null);
        setError(null);
        try {
            const response = await axiosInstance.post('/api/market-data-ingestion/macro/fetch-all');
            if (response.data.success) {
                const data = response.data.data || {};
                const successCount = Object.keys(data.results || {}).length;
                const errorCount = (data.errors || []).length;
                setFetchAllResult({
                    type: 'success',
                    message: response.data.message,
                    details: data,
                    successCount,
                    errorCount,
                });
                fetchDashboard();
                fetchMonitorConfigs();
            } else {
                setFetchAllResult({ type: 'error', message: response.data.message || 'Fetch failed' });
            }
        } catch (err) {
            setFetchAllResult({ type: 'error', message: err.response?.data?.message || 'Failed to fetch macro data' });
        } finally {
            setFetchingAll(false);
        }
    };

    // Config pagination
    const handleConfigPageChange = (event, newPage) => setConfigPage(newPage);
    const handleConfigRowsPerPageChange = (event) => { setConfigRowsPerPage(parseInt(event.target.value, 10)); setConfigPage(0); };

    // Macro data pagination
    const handleMacroDataPageChange = (event, newPage) => {
        setMacroDataPage(newPage);
        fetchMacroData(newPage, macroDataRowsPerPage, macroDataFilter, macroDataSourceFilter);
    };
    const handleMacroDataRowsPerPageChange = (event) => {
        const perPage = parseInt(event.target.value, 10);
        setMacroDataRowsPerPage(perPage);
        setMacroDataPage(0);
        fetchMacroData(0, perPage, macroDataFilter, macroDataSourceFilter);
    };
    const handleMacroDataFilterChange = (event) => {
        const filter = event.target.value;
        setMacroDataFilter(filter);
        setMacroDataPage(0);
        fetchMacroData(0, macroDataRowsPerPage, filter, macroDataSourceFilter);
    };
    const handleMacroDataSourceFilterChange = (event) => {
        const filter = event.target.value;
        setMacroDataSourceFilter(filter);
        setMacroDataPage(0);
        fetchMacroData(0, macroDataRowsPerPage, macroDataFilter, filter);
    };

    // Delete macro data record
    const handleDeleteMacroRecord = async (recordId) => {
        if (!window.confirm('Are you sure you want to delete this record?')) return;
        try {
            const response = await axiosInstance.delete(`/api/market-data-ingestion/macro/data/${recordId}`);
            if (response.data.success) {
                fetchMacroData(macroDataPage, macroDataRowsPerPage, macroDataFilter, macroDataSourceFilter);
                fetchDashboard();
            } else {
                setError(response.data.message || 'Failed to delete record');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete record');
        }
    };

    // Helper: format large numbers
    const formatValue = (val) => {
        if (val == null || val === '') return '-';
        const num = parseFloat(val);
        if (isNaN(num)) return '-';
        return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
    };

    // Helper: change indicator (green up, red down)
    const renderChange = (change, changePercent) => {
        if (change == null && changePercent == null) return null;
        const numChange = parseFloat(change) || 0;
        const isPositive = numChange >= 0;
        const color = isPositive ? '#10b981' : '#ef4444';
        const Icon = isPositive ? ArrowUpward : ArrowDownward;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Icon sx={{ fontSize: 14, color }} />
                <Typography variant="caption" sx={{ fontFamily, color, fontWeight: 600 }}>
                    {formatValue(change)}
                    {changePercent != null && ` (${parseFloat(changePercent).toFixed(2)}%)`}
                </Typography>
            </Box>
        );
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view macro-economic data.</Alert>
            </Box>
        );
    }

    // Render indicator card
    const renderIndicatorCard = (indicatorType) => {
        const data = dashboardData.latest_values[indicatorType];
        const label = MacroIndicatorTypeLabels[indicatorType] || indicatorType;
        const chipColor = MacroIndicatorTypeColors[indicatorType] || 'default';

        return (
            <Grid item xs={12} sm={6} md={3} key={indicatorType}>
                <Card
                    sx={{
                        height: '100%',
                        borderRadius: '16px',
                        border: '1px solid #e2e8f0',
                        boxShadow: 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: ACCENT, boxShadow: `0 4px 20px ${ACCENT}20`, transform: 'translateY(-2px)' },
                    }}
                >
                    <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Chip label={label} size="small" color={chipColor} sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem' }} />
                        </Box>
                        <Typography
                            variant="h5"
                            sx={{ fontFamily, fontWeight: 700, color: '#1e293b', lineHeight: 1.2, mb: 0.5 }}
                        >
                            {data ? formatValue(data.value) : '-'}
                        </Typography>
                        {data && renderChange(data.change_from_previous, data.change_percent)}
                        <Typography
                            variant="caption"
                            sx={{ fontFamily, color: '#94a3b8', fontWeight: 500, display: 'block', mt: 0.5 }}
                        >
                            {data?.period_date ? new Date(data.period_date).toLocaleDateString() : 'No data'}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>
        );
    };

    // Render net liquidity highlighted card
    const renderNetLiquidityCard = () => {
        const nl = dashboardData.latest_net_liquidity;
        const fedBs = dashboardData.latest_values[MacroIndicatorType.FED_BALANCE_SHEET];
        const tga = dashboardData.latest_values[MacroIndicatorType.TGA];
        const rrp = dashboardData.latest_values[MacroIndicatorType.REVERSE_REPO];

        const nlValue = nl?.value;
        const isPositiveChange = nl?.change_from_previous != null ? parseFloat(nl.change_from_previous) >= 0 : null;
        const borderColor = isPositiveChange === true ? '#10b981' : isPositiveChange === false ? '#ef4444' : '#e2e8f0';
        const bgGradient = isPositiveChange === true
            ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
            : isPositiveChange === false
                ? 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)';

        return (
            <Card
                sx={{
                    borderRadius: '16px',
                    border: `2px solid ${borderColor}`,
                    boxShadow: 'none',
                    background: bgGradient,
                    mb: 3,
                }}
            >
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box
                            sx={{
                                p: 1.5,
                                borderRadius: '14px',
                                bgcolor: `${ACCENT}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <AccountBalance sx={{ color: ACCENT, fontSize: 28 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '1.1rem' }}>
                                Fed Net Liquidity
                            </Typography>
                            <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.8rem' }}>
                                Net Liquidity = Fed BS - TGA - RRP
                            </Typography>
                        </Box>
                    </Box>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Typography sx={{ fontFamily, fontWeight: 800, color: '#1e293b', fontSize: '2rem', lineHeight: 1.2 }}>
                                {nlValue != null ? formatValue(nlValue) : '-'}
                            </Typography>
                            {nl && renderChange(nl.change_from_previous, nl.change_percent)}
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={4}>
                                    <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.7)', border: '1px solid #e2e8f0' }}>
                                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>
                                            Fed Balance Sheet
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            {fedBs ? formatValue(fedBs.value) : '-'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.7)', border: '1px solid #e2e8f0' }}>
                                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>
                                            TGA Balance
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            {tga ? formatValue(tga.value) : '-'}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={4}>
                                    <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.7)', border: '1px solid #e2e8f0' }}>
                                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', fontWeight: 500 }}>
                                            Reverse Repo (RRP)
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            {rrp ? formatValue(rrp.value) : '-'}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    };

    // Render Dashboard Tab
    const renderDashboardTab = () => (
        <Box>
            {dashboardLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress sx={{ color: ACCENT }} />
                </Box>
            ) : (
                <>
                    {/* Net Liquidity Highlighted Card */}
                    {renderNetLiquidityCard()}

                    {/* Indicator Groups */}
                    {INDICATOR_GROUPS.map((group) => (
                        <Box key={group.label} sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <group.icon sx={{ color: group.color, fontSize: 22 }} />
                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                    {group.label}
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                {group.indicators.map((indicatorType) => renderIndicatorCard(indicatorType))}
                            </Grid>
                        </Box>
                    ))}
                </>
            )}
        </Box>
    );

    // Render Monitor Config Tab
    const renderMonitorConfigTab = () => {
        const paginatedConfigs = monitorConfigs.slice(
            configPage * configRowsPerPage,
            configPage * configRowsPerPage + configRowsPerPage
        );

        return (
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Indicator Type</TableCell>
                                <TableCell>Data Source</TableCell>
                                <TableCell>Refresh Interval (min)</TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell>Alert on Release</TableCell>
                                <TableCell>Alert Threshold %</TableCell>
                                <TableCell>Last Fetched</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {configsLoading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : paginatedConfigs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <AccountBalance sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No monitor configurations found</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedConfigs.map((config) => (
                                    <TableRow key={config.id} hover>
                                        <TableCell>
                                            <Chip
                                                label={MacroIndicatorTypeLabels[config.indicator_type] || config.indicator_type}
                                                size="small"
                                                color={MacroIndicatorTypeColors[config.indicator_type] || 'default'}
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={MacroDataSourceLabels[config.data_source] || config.data_source || '-'}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {config.refresh_interval_minutes != null ? `${config.refresh_interval_minutes} min` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={config.is_active ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={config.is_active ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={config.alert_on_release ? 'Yes' : 'No'}
                                                size="small"
                                                color={config.alert_on_release ? 'info' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {config.alert_threshold_percent != null ? `${config.alert_threshold_percent}%` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {config.last_fetched_at ? new Date(config.last_fetched_at).toLocaleString() : 'Never'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Tooltip title="Fetch Now">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleFetchIndicator(config.indicator_type)}
                                                        disabled={fetchingIndicator === config.indicator_type}
                                                    >
                                                        {fetchingIndicator === config.indicator_type
                                                            ? <CircularProgress size={18} />
                                                            : <Refresh />}
                                                    </IconButton>
                                                </Tooltip>
                                                {canEdit && (
                                                    <Tooltip title="Edit Config">
                                                        <IconButton size="small" onClick={() => handleEditConfigOpen(config)}>
                                                            <Edit />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={monitorConfigs.length}
                    page={configPage}
                    onPageChange={handleConfigPageChange}
                    rowsPerPage={configRowsPerPage}
                    onRowsPerPageChange={handleConfigRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>
        );
    };

    // Render Macro Data Tab
    const renderMacroDataTab = () => (
        <Paper>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Indicator</InputLabel>
                    <Select
                        value={macroDataFilter}
                        label="Filter by Indicator"
                        onChange={handleMacroDataFilterChange}
                    >
                        <MenuItem value="">All Indicators</MenuItem>
                        {Object.entries(MacroIndicatorType).map(([key, value]) => (
                            <MenuItem key={key} value={value}>{MacroIndicatorTypeLabels[value]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Filter by Source</InputLabel>
                    <Select
                        value={macroDataSourceFilter}
                        label="Filter by Source"
                        onChange={handleMacroDataSourceFilterChange}
                    >
                        <MenuItem value="">All Sources</MenuItem>
                        {Object.entries(MacroDataSource).map(([key, value]) => (
                            <MenuItem key={key} value={value}>{MacroDataSourceLabels[value]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Tooltip title="Refresh">
                    <IconButton size="small" onClick={() => fetchMacroData(macroDataPage, macroDataRowsPerPage, macroDataFilter, macroDataSourceFilter)}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', fontFamily }}>
                    {macroDataTotal} record{macroDataTotal !== 1 ? 's' : ''} found
                </Typography>
            </Box>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Indicator</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell align="right">Value</TableCell>
                            <TableCell align="right">Previous</TableCell>
                            <TableCell align="right">Change</TableCell>
                            <TableCell align="right">Change %</TableCell>
                            <TableCell>Period Date</TableCell>
                            <TableCell>Release Date</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {macroDataLoading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : macroDataRecords.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                                    <Storage sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                    <Typography color="text.secondary">No macro data records found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            macroDataRecords.map((record) => (
                                <TableRow key={record.id} hover>
                                    <TableCell>
                                        <Chip
                                            label={MacroIndicatorTypeLabels[record.indicator_type] || record.indicator_type}
                                            size="small"
                                            color={MacroIndicatorTypeColors[record.indicator_type] || 'default'}
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={MacroDataSourceLabels[record.data_source] || record.data_source || '-'}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontFamily, fontWeight: 600 }}>
                                            {formatValue(record.value)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontFamily }}>
                                            {formatValue(record.previous_value)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {renderChange(record.change_value, null)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{
                                            fontFamily,
                                            color: record.change_percent > 0 ? '#10b981' : record.change_percent < 0 ? '#ef4444' : '#64748b',
                                            fontWeight: 600,
                                        }}>
                                            {record.change_percent != null ? `${parseFloat(record.change_percent).toFixed(2)}%` : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily }}>
                                            {record.period_date ? new Date(record.period_date).toLocaleDateString() : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily }}>
                                            {record.release_date ? new Date(record.release_date).toLocaleDateString() : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {canEdit && (
                                            <Tooltip title="Delete">
                                                <IconButton size="small" color="error" onClick={() => handleDeleteMacroRecord(record.id)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={macroDataTotal}
                page={macroDataPage}
                onPageChange={handleMacroDataPageChange}
                rowsPerPage={macroDataRowsPerPage}
                onRowsPerPageChange={handleMacroDataRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </Paper>
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <AccountBalance sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                    Macro-Economic Data Feed
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    Monitor Fed balance sheet, liquidity, VIX, treasury rates, and FOMC calendar
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {canAdd && (
                            <Button
                                variant="contained"
                                onClick={handleManualEntryOpen}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: '#fff',
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                }}
                            >
                                Add Data
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            onClick={handleFetchAll}
                            disabled={fetchingAll}
                            startIcon={fetchingAll ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <CloudDownload />}
                            sx={{
                                bgcolor: 'rgba(255,255,255,0.25)',
                                color: '#fff',
                                fontFamily,
                                fontWeight: 600,
                                textTransform: 'none',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.2)',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                                '&.Mui-disabled': { color: 'rgba(255,255,255,0.6)' },
                            }}
                        >
                            {fetchingAll ? 'Fetching...' : 'Fetch All from FRED'}
                        </Button>
                        <Tooltip title="Refresh">
                            <IconButton
                                onClick={() => { fetchDashboard(); fetchMonitorConfigs(); }}
                                sx={{ color: '#fff' }}
                            >
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Error */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Fetch Result */}
                {fetchAllResult && (
                    <Alert
                        severity={fetchAllResult.type === 'success' ? 'success' : 'error'}
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        onClose={() => setFetchAllResult(null)}
                    >
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.875rem' }}>
                            {fetchAllResult.message}
                        </Typography>
                        {fetchAllResult.details?.errors?.length > 0 && (
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', mt: 0.5, color: '#b45309' }}>
                                Failed: {fetchAllResult.details.errors.map(e => e.indicator).join(', ')}
                            </Typography>
                        )}
                    </Alert>
                )}

                {/* Tabs */}
                <Paper sx={{ mb: 3, borderRadius: '12px' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        sx={{
                            '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none' },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { bgcolor: ACCENT },
                        }}
                    >
                        <Tab label="Macro Dashboard" icon={<TrendingUp />} iconPosition="start" />
                        <Tab label="Macro Data" icon={<Storage />} iconPosition="start" />
                        <Tab label="Monitor Config" icon={<Speed />} iconPosition="start" />
                    </Tabs>
                </Paper>

                {/* Tab Content */}
                {activeTab === 0 && renderDashboardTab()}
                {activeTab === 1 && renderMacroDataTab()}
                {activeTab === 2 && renderMonitorConfigTab()}
            </Box>

            {/* Manual Entry Dialog */}
            <Dialog open={manualEntryOpen} onClose={handleManualEntryClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Add Macro Data Entry</DialogTitle>
                <DialogContent>
                    {manualFormErrors.submit && <Alert severity="error" sx={{ mb: 2 }}>{manualFormErrors.submit}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth error={!!manualFormErrors.indicator_type} required>
                                <InputLabel>Indicator Type</InputLabel>
                                <Select
                                    value={manualForm.indicator_type}
                                    label="Indicator Type"
                                    onChange={handleManualFormChange('indicator_type')}
                                >
                                    {Object.entries(MacroIndicatorType).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{MacroIndicatorTypeLabels[value]}</MenuItem>
                                    ))}
                                </Select>
                                {manualFormErrors.indicator_type && (
                                    <Typography variant="caption" color="error">{manualFormErrors.indicator_type}</Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Value"
                                type="number"
                                value={manualForm.value}
                                onChange={handleManualFormChange('value')}
                                error={!!manualFormErrors.value}
                                helperText={manualFormErrors.value || 'e.g. 6815723 (millions) or 18.42 (index)'}
                                placeholder="e.g. 6815723"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Previous Value"
                                type="number"
                                value={manualForm.previous_value}
                                onChange={handleManualFormChange('previous_value')}
                                helperText="e.g. 6822145 (previous period value)"
                                placeholder="e.g. 6822145"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Change"
                                type="number"
                                value={manualForm.change_from_previous}
                                onChange={handleManualFormChange('change_from_previous')}
                                helperText="e.g. -6422 (value - previous value)"
                                placeholder="e.g. -6422"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Change %"
                                type="number"
                                value={manualForm.change_percent}
                                onChange={handleManualFormChange('change_percent')}
                                helperText="e.g. -0.09 (percentage change)"
                                placeholder="e.g. -0.09"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Release Date"
                                type="date"
                                value={manualForm.release_date}
                                onChange={handleManualFormChange('release_date')}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Period Date"
                                type="date"
                                value={manualForm.period_date}
                                onChange={handleManualFormChange('period_date')}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Data Source</InputLabel>
                                <Select
                                    value={manualForm.data_source}
                                    label="Data Source"
                                    onChange={handleManualFormChange('data_source')}
                                >
                                    {Object.entries(MacroDataSource).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{MacroDataSourceLabels[value]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleManualEntryClose} disabled={submitting}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleManualEntrySubmit}
                        disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Add Entry'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Monitor Config Dialog */}
            <Dialog open={editConfigOpen} onClose={handleEditConfigClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    Edit Config: {MacroIndicatorTypeLabels[selectedConfig?.indicator_type] || selectedConfig?.indicator_type}
                </DialogTitle>
                <DialogContent>
                    {editConfigErrors.submit && <Alert severity="error" sx={{ mb: 2 }}>{editConfigErrors.submit}</Alert>}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Refresh Interval (minutes)"
                                type="number"
                                value={editConfigForm.refresh_interval_minutes}
                                onChange={handleEditConfigFormChange('refresh_interval_minutes')}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Data Source</InputLabel>
                                <Select
                                    value={editConfigForm.data_source}
                                    label="Data Source"
                                    onChange={handleEditConfigFormChange('data_source')}
                                >
                                    {Object.entries(MacroDataSource).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>{MacroDataSourceLabels[value]}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Alert Threshold %"
                                type="number"
                                value={editConfigForm.alert_threshold_percent}
                                onChange={handleEditConfigFormChange('alert_threshold_percent')}
                                inputProps={{ step: '0.01', min: '0' }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editConfigForm.is_active}
                                        onChange={handleEditConfigFormChange('is_active')}
                                        color="success"
                                    />
                                }
                                label="Active"
                                sx={{ fontFamily }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editConfigForm.alert_on_release}
                                        onChange={handleEditConfigFormChange('alert_on_release')}
                                        color="info"
                                    />
                                }
                                label="Alert on Release"
                                sx={{ fontFamily }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditConfigClose} disabled={submitting}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleEditConfigSubmit}
                        disabled={submitting}
                        sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MacroEconomicPage;
